/**
 * PATH: packages/pricelist/src/ColumnManagerDialog.tsx
 * FILE_ID: PKG-PRICELIST-COLMGR-001
 * PURPOSE: Manage a section's existing columns in one place — reorder, edit
 *          label/type/formula, and remove. Saves the whole column_schema back
 *          via updateSection (keys kept stable, so item data stays valid).
 * WHY: Operators asked to "structure pricelists properly", not just add columns
 *      one at a time. Add stays in ColumnDialog; this is the editor for what's
 *      already there.
 * RELATED: columnSchemaOps (pure ops), formulaFields, PricelistWorkbook, types
 */
import { useEffect, useState } from 'react'
import { GripVertical, Trash2, Loader2 } from 'lucide-react'
import type { ColumnDef } from './types'
import type { FormulaField } from './formulaFields'
import { moveColumnTo, removeColumnAt, updateColumnAt } from './columnSchemaOps'
import HiddenColumnsTab from './HiddenColumnsTab'

const ALIGNS: Array<{ value: '' | NonNullable<ColumnDef['align']>; label: string }> = [
  { value: '', label: 'Auto' },
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' },
]

const TYPES: Array<{ value: NonNullable<ColumnDef['data_type']>; label: string }> = [
  { value: 'number', label: 'Number' },
  { value: 'money', label: 'Money' },
  { value: 'text', label: 'Text' },
  { value: 'enum', label: 'Dropdown' },
  { value: 'bool', label: 'Yes/No' },
]

export interface ColumnManagerDialogProps {
  open: boolean
  columns: ColumnDef[]
  availableFields?: FormulaField[]
  saving?: boolean
  /** Offer the org-wide "apply to all sections" save (owner/admin hosts). */
  canApplyToAllSections?: boolean
  /** Restrict the dialog to schema order + hide/unhide controls. */
  arrangeOnly?: boolean
  /** Read-only: any viewer may open this to SEE every column (incl. hidden)
   *  but cannot reorder, edit, hide/unhide or save. No drag, no inputs, no
   *  Save — just the column list + the Hidden columns tab. */
  viewOnly?: boolean
  onClose: () => void
  onSave: (next: ColumnDef[], opts?: { applyToAllSections?: boolean }) => void | Promise<void>
}

export default function ColumnManagerDialog({
  open,
  columns,
  availableFields = [],
  saving = false,
  canApplyToAllSections = false,
  arrangeOnly = false,
  viewOnly = false,
  onClose,
  onSave,
}: ColumnManagerDialogProps) {
  const [draft, setDraft] = useState<ColumnDef[]>(columns)
  // The org schema is uniform across sections, so org-wide apply is the
  // DEFAULT (standard) save; unticking is the per-section exception.
  const [applyAll, setApplyAll] = useState(true)
  const [tab, setTab] = useState<'columns' | 'hidden'>('columns')
  // Drag-and-drop reorder: index being dragged + current hover target.
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [dropIndex, setDropIndex] = useState<number | null>(null)
  useEffect(() => {
    if (open) {
      setDraft(columns)
      setApplyAll(true)
      setTab('columns')
      setDragIndex(null)
      setDropIndex(null)
    }
  }, [open, columns])

  if (!open) return null

  const patch = (i: number, p: Partial<ColumnDef>) => setDraft((d) => updateColumnAt(d, i, p))

  const inputCls =
    'rounded-md border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-800 dark:text-white'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="column-manager-title"
        className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-xl border border-slate-200 bg-white p-5 shadow-xl dark:border-slate-700 dark:bg-slate-900"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose()
        }}
      >
        <h3 id="column-manager-title" className="mb-1 text-base font-semibold text-slate-900 dark:text-white">
          {viewOnly ? 'Columns' : arrangeOnly ? 'Rearrange schema' : 'Manage columns'}
        </h3>
        {viewOnly && (
          <p className="mb-3 text-[11px] text-slate-400">
            View every column and what’s hidden. Reordering and hide/unhide are
            reserved for org admins/owners.
          </p>
        )}
        {!arrangeOnly && !viewOnly && availableFields.length > 0 && (
          <p className="mb-3 text-[11px] text-slate-400">
            Formula fields:{' '}
            {availableFields.map((f) => (
              <code key={f.key} className="mr-1">
                {f.key}
              </code>
            ))}
          </p>
        )}

        <div className="mb-2 flex gap-1 border-b border-slate-200 dark:border-slate-700">
          {([
            ['columns', 'Columns'],
            ['hidden', `Hidden columns (${draft.filter((c) => c.hidden_by_default === true).length})`],
          ] as const).map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={
                '-mb-px rounded-t-md px-3 py-1.5 text-xs ' +
                (tab === id
                  ? 'border border-b-white bg-white font-medium text-slate-900 dark:border-slate-700 dark:border-b-slate-900 dark:bg-slate-900 dark:text-white'
                  : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200')
              }
            >
              {label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto">
          {tab === 'hidden' ? (
            <HiddenColumnsTab
              columns={draft}
              viewOnly={viewOnly}
              onShow={(keys) =>
                setDraft((d) =>
                  d.map((col) =>
                    keys.includes(col.key) ? { ...col, hidden_by_default: false } : col,
                  ),
                )
              }
            />
          ) : draft.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-400">
              No custom columns yet. Close this and use “Add column”.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {draft.map((col, i) => (
                <div
                  key={col.key}
                  onDragOver={(e) => {
                    if (viewOnly || dragIndex === null) return
                    e.preventDefault()
                    e.dataTransfer.dropEffect = 'move'
                    if (dropIndex !== i) setDropIndex(i)
                  }}
                  onDrop={(e) => {
                    if (viewOnly) return
                    e.preventDefault()
                    if (dragIndex !== null) setDraft((d) => moveColumnTo(d, dragIndex, i))
                    setDragIndex(null)
                    setDropIndex(null)
                  }}
                  className={
                    'rounded-lg border p-2 ' +
                    (dropIndex === i && dragIndex !== null && dragIndex !== i
                      ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/30'
                      : 'border-slate-200 dark:border-slate-700') +
                    (dragIndex === i ? ' opacity-50' : '')
                  }
                >
                  <div className="flex flex-wrap items-center gap-2">
                    {!viewOnly && (
                      <span
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.effectAllowed = 'move'
                          // Firefox needs data for the drag to start.
                          e.dataTransfer.setData('text/plain', col.key)
                          setDragIndex(i)
                        }}
                        onDragEnd={() => {
                          setDragIndex(null)
                          setDropIndex(null)
                        }}
                        title={`Drag to move ${col.label || col.key}`}
                        aria-label={`Drag to move ${col.label || col.key}`}
                        className="cursor-grab text-slate-400 hover:text-slate-700 active:cursor-grabbing dark:hover:text-slate-200"
                      >
                        <GripVertical className="h-4 w-4" />
                      </span>
                    )}
                    {arrangeOnly || viewOnly ? (
                      <span className="flex-1 text-sm font-medium text-slate-800 dark:text-slate-100">
                        {col.label || col.key}
                        {viewOnly && col.hidden_by_default === true && (
                          <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-normal text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                            hidden
                          </span>
                        )}
                      </span>
                    ) : (
                      <>
                        <input
                          value={col.label || ''}
                          onChange={(e) => patch(i, { label: e.target.value })}
                          placeholder="Label"
                          aria-label={`Label for ${col.key}`}
                          className={`${inputCls} w-32`}
                        />
                        <select
                          value={col.data_type || 'number'}
                          onChange={(e) => patch(i, { data_type: e.target.value as ColumnDef['data_type'] })}
                          aria-label={`Type for ${col.key}`}
                          className={inputCls}
                        >
                          {TYPES.map((t) => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                          ))}
                        </select>
                        <input
                          value={col.computed_from || ''}
                          onChange={(e) => patch(i, { computed_from: e.target.value || undefined })}
                          placeholder="=formula (optional)"
                          aria-label={`Formula for ${col.key}`}
                          className={`${inputCls} flex-1 font-mono`}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm(
                              `Delete column "${col.label || col.key}" from the pricelist?\n\n` +
                                'Row values are kept on the server; re-adding a column with the ' +
                                `key "${col.key}" restores them.`,
                            )) setDraft((d) => removeColumnAt(d, i))
                          }}
                          className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40"
                          aria-label={`Remove ${col.key}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                  {!viewOnly && (
                  <div className="mt-2 flex flex-wrap items-center gap-3 pl-6 text-[11px] text-slate-500 dark:text-slate-400">
                    {!arrangeOnly && <label className="flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={!!col.required}
                        onChange={(e) => patch(i, { required: e.target.checked })}
                      />
                      Required
                    </label>}
                    <label className="flex items-center gap-1" title="Hide this column by default for all users (data is kept)">
                      <input
                        type="checkbox"
                        aria-label={`${col.hidden_by_default ? 'Unhide' : 'Hide'} ${col.label || col.key}`}
                        checked={!!col.hidden_by_default}
                        onChange={(e) => patch(i, { hidden_by_default: e.target.checked })}
                      />
                      Hidden
                    </label>
                    <label className="flex items-center gap-1" title="Cell alignment (Auto = numbers centred, text left)">
                      Align
                      <select
                        value={col.align ?? ''}
                        onChange={(e) =>
                          patch(i, { align: (e.target.value || undefined) as ColumnDef['align'] })
                        }
                        aria-label={`Alignment for ${col.key}`}
                        className={inputCls}
                      >
                        {ALIGNS.map((a) => (
                          <option key={a.value} value={a.value}>{a.label}</option>
                        ))}
                      </select>
                    </label>
                    {!arrangeOnly && (col.data_type === 'number' || col.data_type === 'money') && (
                      <>
                        <label className="flex items-center gap-1">
                          Min
                          <input
                            type="number"
                            value={col.min ?? ''}
                            onChange={(e) => patch(i, { min: e.target.value === '' ? null : Number(e.target.value) })}
                            aria-label={`Min for ${col.key}`}
                            className={`${inputCls} w-16`}
                          />
                        </label>
                        <label className="flex items-center gap-1">
                          Max
                          <input
                            type="number"
                            value={col.max ?? ''}
                            onChange={(e) => patch(i, { max: e.target.value === '' ? null : Number(e.target.value) })}
                            aria-label={`Max for ${col.key}`}
                            className={`${inputCls} w-16`}
                          />
                        </label>
                      </>
                    )}
                    {!arrangeOnly && <label className="flex items-center gap-1">
                      Feeds quote
                      <select
                        value={col.quote_field ?? ''}
                        onChange={(e) =>
                          patch(i, { quote_field: (e.target.value || null) as ColumnDef['quote_field'] })
                        }
                        aria-label={`Quote mapping for ${col.key}`}
                        className={inputCls}
                      >
                        <option value="">—</option>
                        <option value="sell">Sell rate</option>
                        <option value="cost">Cost</option>
                        <option value="productivity">Productivity</option>
                        <option value="unit">Unit</option>
                      </select>
                    </label>}
                  </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 flex items-center justify-end gap-3">
          {!viewOnly && canApplyToAllSections && (
            <label
              className="mr-auto flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400"
              title="Save this column order + hidden set as the org-wide default view for every section and every user"
            >
              <input
                type="checkbox"
                checked={applyAll}
                onChange={(e) => setApplyAll(e.target.checked)}
              />
              Apply order &amp; hidden columns to ALL sections (org default)
            </label>
          )}
          <button
            onClick={onClose}
            autoFocus={arrangeOnly || viewOnly}
            className="rounded-md px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            {viewOnly ? 'Close' : 'Cancel'}
          </button>
          {!viewOnly && (
            <button
              onClick={() => onSave(draft, { applyToAllSections: applyAll })}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {arrangeOnly ? 'Save schema' : 'Save columns'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
